import React, { useEffect, useRef, useState } from 'react';
import mixpanel from "mixpanel-browser";
import { useForm } from "react-hook-form";
import { useDispatch } from 'react-redux';
import Spinner from '../../components/Spinner';
import { AuthInput, PasswordInput } from '../../components/ui';
import { API, Auth } from '../../utils/amplifyWrapper';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';
import ReCAPTCHA from "react-google-recaptcha";
import { fetchIpInfoService } from '../../utils/Country'
import { getFirstTouch } from '../../utils/FirstTouch'
import { getSignupSource } from '../../utils/SignupSource'
import { getGa4Attribution } from '../../utils/Ga4Client'
import { getMpDistinctId } from '../../utils/mpIdentity';
import { markLoginTrackedForToday } from '../../services/mixpanel';
import { addMixPanelEvent } from '../../store/actions';
import { trackGA4Event, trackGA4EventAsync, setGA4UserProperties, gateRedirectOnGA4Events } from '../../utils/trackGA4Event';
import { getGa4PlanTier } from '../../utils/ga4PlanTier';
import { EMAIL_VERIFIED, PLAN_SELECTED, SIGN_UP_COMPLETE, SIGN_UP_LOAD } from '../../constants/gtag-events';
import AuthSpritesImg from "../../assets/auth-sprites.svg"
import { ACCOUNT_CREATED } from '../../constants/hotjar-events';
import { useAuthStore } from '../../zustand-store/useAuthStore';
import GoogleOAuthButton from './GoogleOAuthButton';
const classNames = require('classnames');

export default function SignUp() {

    let query = new URLSearchParams(new URL(window.location.href).search);
    const { setUser: setGlobalUser, setAuthState: setGlobalAuthState } = useAuthStore();
    const dispatch = useDispatch();

    // Prefer the landing page the marketing site captured; fall back to the
    // browser referrer for direct traffic that never picked up a first-touch param.
    const firstTouchForGa4 = getFirstTouch();
    const signupSource = firstTouchForGa4?.first_landing_page || firstTouchForGa4?.first_referrer || window.document.referrer || 'direct';

    const [isLoading, setIsLoading] = useState(false);
    const signUpForm = useForm();
    const { formState: { errors: signUpErrors } } = signUpForm;
    const confirmSignUpForm = useForm();
    const { formState: { errors: confirmSignUpErrors } } = confirmSignUpForm;
    const [recaptchaToken, setRecaptchaToken] = useState(null)
    const [formState, setFormState] = useState("signup");
    const [organisation, setOrganisation] = useState(null);
    const [username, setUsername] = useState(null);
    const [password, setPassword] = useState(null);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [isResendingCode, setIsResendingCode] = useState(false);
    const recaptchaRef = useRef()
    // When the form became visible — sent to the server to catch too-fast bot submissions
    const formLoadTimeRef = useRef(Date.now())
    // Guards the GA4-gated redirect below against firing after the user has
    // navigated away/unmounted (mirrors GoogleCallback.js's isMountedRef).
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setInterval(() => {
            setResendCooldown(seconds => (seconds <= 1 ? 0 : seconds - 1));
        }, 1000);
        return () => clearInterval(timer);
        // Re-run only when cooldown starts/ends, not on every tick.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resendCooldown > 0])


    useEffect(() => {
        // Clear any stale auth state when component mounts
        setGlobalUser(null);
        
        trackGA4Event(SIGN_UP_LOAD, {
            method: 'email',
            signup_source: signupSource,
            ...getSignupSource(),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setGlobalUser])



    const onSubmitSignUpForm = async data => {

        setIsLoading(true);

        // Recaptcha token should be generated only once
        let token = recaptchaToken
        if (!recaptchaToken) {
            token = await recaptchaRef.current.executeAsync()
            setRecaptchaToken(token)
        }

        const { username, password, name, family_name, hp_field } = data;

        const email_verificaiton = await API.post('secondary', `/verify_email`, {
            body: { emailId: username }
        })

        if (email_verificaiton.data.result !== "valid") {
            toast.error("Please enter a valid email address")
            setIsLoading(false);
            return;
        }

        let localOrganisation = `${name}'s organisation`
        setOrganisation(localOrganisation);
        setUsername(username);
        setPassword(password);

        console.log({ token })

        Auth.signUp({
            username,
            password,
            attributes: {
                given_name: name,
                family_name,
                'custom:org': localOrganisation,
                'custom:phone': '',
                'custom:usecase': '',
                'custom:referrer_url': window.document.referrer,
                'custom:is_v2': '1'
            },
            validationData: [{
                Name: 'recaptchaToken',
                Value: token,
            }, {
                Name: 'honeypot',
                Value: hp_field || '',
            }, {
                Name: 'formElapsedMs',
                Value: String(Date.now() - formLoadTimeRef.current),
            }]
        }).then((response) => {

            setFormState("confirmSignUp");

            mixpanel.alias(username);

            window.hj("event", ACCOUNT_CREATED);

        }).catch((error) => {

            // Bot-check false positives (e.g. password manager) had no retry path — clear state so the next submit can succeed.
            if (/Bot detected|Form submitted too fast/i.test(error.message || '')) {
                signUpForm.setValue('hp_field', '');
                setRecaptchaToken(null);
                recaptchaRef.current?.reset();
                toast.error("Something went wrong, please try again.");
            } else if (error.name === 'InvalidParameterException' && /password/i.test(error.message || '')) {
                toast.error("Password cannot start or end with a space, and must be at least 8 characters.");
            } else {
                toast.error(error.message);
            }
            // Clear any partial auth state on signup failure
            setGlobalUser(null);

        }).finally(() => {

            setIsLoading(false);

        });

    };

    const RESEND_COOLDOWN_SECONDS = 45;

    const onResendCode = async () => {

        if (resendCooldown > 0 || isResendingCode) {
            return;
        }

        setIsResendingCode(true);

        Auth.resendSignUp(username)
            .then(data => {

                // Only start the cooldown once the code was actually resent, so a
                // transient failure doesn't also lock the user out of retrying.
                setResendCooldown(RESEND_COOLDOWN_SECONDS);
                toast.success("Code has been resent successfully.");

            })
            .catch(error => {

                toast.error(error.message);

            }).finally(() => {

                // Not touching isLoading here — this function never sets it, and clearing it
                // would wrongly re-enable the confirm-code submit if that's in flight too.
                setIsResendingCode(false);

            });

    }


    const onSubmitConfirmSignUp = async data => {

        const { code } = data;

        setIsLoading(true);

        const userLocationData = await fetchIpInfoService()

        const location = {
            country: userLocationData.country,
        }

        const storedFirstTouch = getFirstTouch();
        const firstTouch = {
            ...storedFirstTouch,
            first_country: storedFirstTouch?.first_country || location.country,
        };

        Auth.confirmSignUp(username, code.trim()).then(() => {

            trackGA4Event(EMAIL_VERIFIED, { method: 'email' });

            return Auth.signIn(username, password).then(userResponse => {

                setGlobalUser(userResponse);
                // GROW-81: this is a brand-new account, not a return — consume today's login
                // slot silently so the session-resume path doesn't count signup day as a Login.
                markLoginTrackedForToday();
                // GROW-81: Account - Email Verified — fires on the email/OTP path only
                // (Google emails are pre-verified). method is always email here.
                dispatch(addMixPanelEvent({ body: { eventName: 'ACCOUNT_EMAIL_VERIFIED', eventData: { method: 'email' } } }));

                // Returned so a synchronous throw anywhere in this chain (e.g. window.hj,
                // setGA4UserProperties) propagates to the outer .catch() below instead of
                // becoming an unhandled rejection that leaves isLoading stuck true.
                return API.post(`main`, `/organisation`, {
                    body: { name: organisation, location, source: 'email', firstTouch, ...getGa4Attribution(), mp_distinct_id: getMpDistinctId() },
                    headers: { "Content-Type": "application/json" },
                }).then(response => {

                    if (!response.success) {
                        Sentry.captureException(response);
                        toast.error("Something went wrong!")
                        setIsLoading(false);
                        return;
                    }

                    window.hj('identify', response.data.orgId, {
                        'Page': 'Sign in',
                    });

                    // plan_id is only known here if the user arrived with one pre-selected
                    // (e.g. from a pricing page); otherwise they're on the default free plan.
                    const selectedPlanId = query.get('plan_id') || undefined;
                    const ga4PlanTier = getGa4PlanTier(selectedPlanId);

                    setGA4UserProperties({
                        organisation_id: response.data.orgId,
                        user_plan_tier: ga4PlanTier,
                        // no checkout yet — a pre-selected paid plan just means one's pending
                        user_type: (selectedPlanId && ga4PlanTier !== 'free') ? 'trial' : 'free',
                        signup_month: new Date().toISOString().slice(0, 7),
                        first_touch_source: firstTouchForGa4?.first_utm_source || firstTouchForGa4?.first_referring_domain || 'direct',
                    });

                    // Update auth state to onboarding
                    setGlobalAuthState('onboarding');

                    // Fired here (org creation succeeded) so it lines up with Mixpanel's
                    // CREATE_ACCOUNT. Fire both events in parallel and gate the redirect on
                    // both together — nesting them serially would stack each call's own
                    // ~1000ms fallback timeout on top of the other. A full page redirect
                    // right after firing can cancel these GA4 network requests mid-flight,
                    // especially on mobile/slow connections, silently dropping the signup
                    // events, which is why we wait for them here.
                    gateRedirectOnGA4Events([
                        trackGA4EventAsync(SIGN_UP_COMPLETE, {
                            method: 'email',
                            signup_source: signupSource,
                            ...getSignupSource(),
                        }),
                        trackGA4EventAsync(PLAN_SELECTED, {
                            change_type: 'initial',
                            ...(selectedPlanId && { plan_id: selectedPlanId }),
                            plan_tier: ga4PlanTier,
                        }),
                    ], 'GA4 signup event(s) did not confirm before email signup redirect', () => {
                        // Redirect to onboarding flow using full page navigation
                        // (window.location.href) to switch from auth router to protected routes
                        const onboardingUrl = `/onboarding/work?orgId=${response.data.orgId}&integrationId=${response.data.id}`;
                        const url = new URL(onboardingUrl, window.location.origin);

                        // Preserve plan_id and quantity if present
                        if (query.has('plan_id')) {
                            url.searchParams.set('plan_id', query.get('plan_id'));
                            if (query.has('quantity')) {
                                url.searchParams.set('quantity', query.get('quantity'));
                            }
                        }

                        // Keep the original ~100ms floor this redirect always had, on top of
                        // the GA4 wait — the events can resolve in just a few ms (GTM sometimes
                        // calls back near-instantly), which alone would be too fast for
                        // setGlobalAuthState('onboarding') above to have propagated, and was
                        // exactly the race the original delay existed to prevent (404 flash).
                        setTimeout(() => {
                            setIsLoading(false);
                            // User may have navigated away while these were in flight — don't
                            // force a redirect on a page they already left.
                            if (isMountedRef.current) {
                                window.location.href = url.href;
                            }
                        }, 100);
                    }, () => {
                        setIsLoading(false);
                        // The account/org already exists and the user is signed in by this
                        // point (setGlobalAuthState('onboarding') above already ran) — don't
                        // revert to 'signin' here, that would strand an authenticated user on
                        // the signin screen. Just surface the failure so they can retry the redirect.
                        if (isMountedRef.current) {
                            toast.error('Something went wrong. Please try again.');
                        }
                    });

                })
            })

        }).catch((error) => {
            setIsLoading(false);
            toast.error(error.message);

        })

    }

    return (
        <div className="row my-3 my-md-0">
            <div className="col-md-8 offset-md-2 col-lg-6 offset-lg-3 px-0">
                {
                    (formState === "signup") &&
                    <form onSubmit={signUpForm.handleSubmit(onSubmitSignUpForm)} >
                        <div style={{ width: 150, height: 6, borderRadius: 10, background: 'var(--bs-border-color)', margin: '0 auto 20px' }}>
                            <div style={{ height: '100%', background: 'var(--bs-primary)', borderRadius: 10, transition: 'width 0.3s ease', width: '0%' }} />
                        </div>
                        <h4 className="fw-bold text-body-emphasis text-center mb-1">Sign up to get started</h4>
                        <p className="text-center mb-4" style={{ color: 'var(--bs-secondary-color)', fontSize: 14 }}>No credit card required</p>
                        <div className="row g-2">
                            <div className="col-12 col-sm-6 mb-3">
                                <AuthInput
                                    label="First name"
                                    name="name"
                                    type="text"
                                    register={signUpForm.register}
                                    errors={signUpErrors.name}
                                    validation={{ required: true, minLength: 3 }}
                                    backgroundImage={AuthSpritesImg}
                                    backgroundPosition="4px 0px"
                                />
                            </div>
                            <div className="col-12 col-sm-6 mb-3">
                                <AuthInput
                                    label="Last name"
                                    name="family_name"
                                    type="text"
                                    register={signUpForm.register}
                                    errors={signUpErrors.family_name}
                                    validation={{ required: true, minLength: 2 }}
                                    backgroundImage={AuthSpritesImg}
                                    backgroundPosition="4px 0px"
                                />
                            </div>
                        </div>
                        <AuthInput
                            label="Email"
                            name="username"
                            type="email"
                            register={signUpForm.register}
                            errors={signUpErrors.username}
                            validation={{ required: true, pattern: /^[\w-.+]+@([\w-]+\.)+[\w-]{2,24}$/g }}
                            backgroundImage={AuthSpritesImg}
                            backgroundPosition="4px -35px"
                        />
                        <PasswordInput
                            label="Password"
                            name="password"
                            register={signUpForm.register}
                            errors={signUpErrors.password}
                            validation={{
                                required: true,
                                minLength: 8,
                                pattern: {
                                    value: /^\S(.*\S)?$/,
                                    message: 'Password cannot start or end with a space.'
                                }
                            }}
                            backgroundImage={AuthSpritesImg}
                            backgroundPosition="4px -70px"
                            placeholder="8 characters or more"
                        />
                        {/* Honeypot field — hidden from real users, bots fill it. Named/positioned to
                            avoid browser/password-manager autofill heuristics (which key off names
                            like "website", "url", "email") firing on it for a real user. */}
                        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
                            <label htmlFor="hp_field">Leave this field empty</label>
                            <input
                                id="hp_field"
                                type="text"
                                {...signUpForm.register('hp_field')}
                                tabIndex={-1}
                                autoComplete="off"
                            />
                        </div>
                        <div className="mb-3">
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                size="invisible"
                                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                            />
                        </div>
                        <button
                            type={isLoading ? "button" : "submit"}
                            className="btn btn-primary w-100"
                            style={{ height: 44 }}
                        >
                            {isLoading ? <Spinner color="white" /> : "Let's get started"}
                        </button>

                        <GoogleOAuthButton buttonText="Continue with Google" />

                        <p className="text-center mt-2 mb-3" style={{ fontSize: 14 }}>
                            Already have an account?{' '}
                            <span className="text-primary fw-semibold" style={{ cursor: 'pointer' }} onClick={() => { setGlobalAuthState("signin") }}>Sign In</span>
                        </p>
                        <p className="text-center mb-4" style={{ fontSize: 12, color: 'var(--bs-secondary-color)' }}>
                            By continuing, you agree to the{' '}
                            <a className="text-primary" style={{ textDecoration: 'underline' }} href={process.env.REACT_APP_TERMS_URL} target="_blank" rel="noopener noreferrer">Terms of Service</a>
                            {' '}and{' '}
                            <a className="text-primary" style={{ textDecoration: 'underline' }} href={process.env.REACT_APP_PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                        </p>
                    </form>
                }
                {
                    (formState === "confirmSignUp") &&
                    <form className="mt-5" onSubmit={confirmSignUpForm.handleSubmit(onSubmitConfirmSignUp)}>
                        <div style={{ width: 150, height: 6, borderRadius: 10, background: 'var(--bs-border-color)', margin: '0 auto 20px' }}>
                            <div style={{ height: '100%', background: 'var(--bs-primary)', borderRadius: 10, transition: 'width 0.3s ease', width: '33%' }} />
                        </div>
                        <h4 className="fw-bold text-body-emphasis text-center mb-1">Verify your email</h4>
                        <p className="text-center mb-4" style={{ color: 'var(--bs-secondary-color)', fontSize: 14 }}>We have sent you an email with a code.</p>
                        <div className="mb-3">
                            <label className="form-label fw-semibold">Please enter the code below</label>
                            <input style={{ backgroundImage: `url(${AuthSpritesImg})`, backgroundPosition: "4px -70px" }} type="text" name="code" className={classNames("form-control", "auth-input", { "is-invalid": confirmSignUpErrors.code })} {...confirmSignUpForm.register("code", { required: true })} />
                            {
                                confirmSignUpErrors.code &&
                                <div className="invalid-feedback">
                                    {
                                        ("required" === confirmSignUpErrors.code.type) && "Code is required."
                                    }
                                </div>
                            }
                        </div>
                        <button
                            type={isLoading ? "button" : "submit"}
                            className="btn btn-primary w-100"
                            style={{ height: 44 }}
                        >
                            {isLoading ? <Spinner color="white" /> : 'Continue'}
                        </button>
                        <p className="mt-3 text-center" style={{ fontSize: 13, color: 'var(--bs-secondary-color)' }}>
                            Haven't received the code? Check your spam folder or{' '}
                            {resendCooldown > 0
                                ? <span style={{ color: 'var(--bs-secondary-color)' }}>resend available in {resendCooldown}s</span>
                                : isResendingCode
                                    ? <span style={{ color: 'var(--bs-secondary-color)' }}>sending…</span>
                                    : <span className="text-primary" style={{ cursor: 'pointer' }} onClick={() => { onResendCode() }}>click here to resend</span>
                            } the code.
                        </p>
                    </form>
                }
            </div>
        </div>
    );

}
